import * as React from 'react'

import * as TooltipPrimitive from '@radix-ui/react-tooltip'

import { cn } from '@/shared/utils/helpers'

export interface TooltipProps {
	children: React.ReactElement
	title?: React.ReactNode
	content?: React.ReactNode
	open?: boolean
	defaultOpen?: boolean
	onOpenChange?: (open: boolean) => void
	className?: string
	delayDuration?: number
	side?: 'top' | 'right' | 'bottom' | 'left'
	align?: 'start' | 'center' | 'end'
}

// Provider component to wrap the app
export function TooltipProvider({
	children,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
	return (
		<TooltipPrimitive.Provider {...props}>{children}</TooltipPrimitive.Provider>
	)
}

const Tooltip: React.FC<TooltipProps> = ({
	children,
	title,
	content,
	open,
	defaultOpen,
	onOpenChange,
	className,
	delayDuration = 200,
	side = 'top',
	align = 'center',
}) => {
	const tooltipContent = title || content

	// If no content, just return children
	if (!tooltipContent) {
		return children
	}

	return (
		<TooltipPrimitive.Provider delayDuration={delayDuration}>
			<TooltipPrimitive.Root
				open={open}
				defaultOpen={defaultOpen}
				onOpenChange={onOpenChange}
			>
				<TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
				<TooltipPrimitive.Portal>
					<TooltipPrimitive.Content
						side={side}
						align={align}
						className={cn(
							'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-secondary shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
							className
						)}
						sideOffset={5}
					>
						{tooltipContent}
						<TooltipPrimitive.Arrow className="fill-primary" />
					</TooltipPrimitive.Content>
				</TooltipPrimitive.Portal>
			</TooltipPrimitive.Root>
		</TooltipPrimitive.Provider>
	)
}

// Export named components for advanced usage
export const TooltipRoot = TooltipPrimitive.Root
export const TooltipTrigger = TooltipPrimitive.Trigger
export const TooltipContent = TooltipPrimitive.Content
export const TooltipPortal = TooltipPrimitive.Portal
export const TooltipArrow = TooltipPrimitive.Arrow

export default Tooltip
